// AI Recording Studio Pro — Multi-Panel Recording Engine
// Professional multi-source recorder with configurable panel grid
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { studioSettingsAPI, studioConvertAPI } from "@/lib/api";
import { useRecordingStore } from "@/stores/recording-store";
import { useMagnifierStore } from "@/stores/magnifier-store";
import { usePrivacyStore } from "@/stores/privacy-store";
import SmartMagnifier from "@/components/recording-studio/SmartMagnifier";
import PrivacyGuard from "@/components/recording-studio/PrivacyGuard";
import FloombySnipper from "@/components/recording-studio/FloombySnipper";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import StudioPanel from "@/components/recording-studio/StudioPanel";
import RecordingControls from "@/components/recording-studio/RecordingControls";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import {
  MonitorPlay,
  Columns,
  Grid3X3,
  LayoutGrid,
  PanelLeftClose,
  Zap,
  Film,
  CheckCircle2,
  ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";

// ── Layout presets ──
const LAYOUT_PRESETS = [
  { id: "2col", label: "2 Écrans", icon: Columns, count: 2, desc: "Côte à côte" },
  { id: "3col", label: "3 Écrans", icon: PanelLeftClose, count: 3, desc: "Triple colonne" },
  { id: "2x2", label: "4 Écrans", icon: LayoutGrid, count: 4, desc: "Grille 2×2" },
  { id: "1+3", label: "1 + 3", icon: Grid3X3, count: 4, desc: "Principal + 3 petits" },
];

// ── Resolution / FPS / Codec configs ──
const RESOLUTIONS = [
  { value: "720p", label: "720p HD", width: 1280, height: 720 },
  { value: "1080p", label: "1080p Full HD", width: 1920, height: 1080 },
  { value: "2k", label: "2K QHD", width: 2560, height: 1440 },
  { value: "4k", label: "4K UHD", width: 3840, height: 2160 },
];

const CODEC_OPTIONS = [
  { value: "vp8", label: "VP8", mime: "video/webm;codecs=vp8,opus" },
  { value: "vp9", label: "VP9", mime: "video/webm;codecs=vp9,opus" },
  { value: "h264", label: "H.264", mime: "video/webm;codecs=h264,opus" },
];

export default function RecordingStudioPage() {
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  const {
    settings,
    setSettings,
    panels,
    layoutMode,
    setLayoutMode,
    registerRecording,
    fetchProjects,
    projects,
    countdown,
    setCountdownActive,
    countdownActive,
    markers,
    clearMarkers,
  } = useRecordingStore();

  const magnifier = useMagnifierStore();

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedProject, setSelectedProject] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [countdownValue, setCountdownValue] = useState(0);
  const [resizingPanel, setResizingPanel] = useState(null);
  const [immersive, setImmersive] = useState(false);
  const [isSnipping, setIsSnipping] = useState(false);
  // Exposed to the magnifier so it can reuse the recording capture (no 2nd prompt)
  const [captureStream, setCaptureStream] = useState(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const studioContainerRef = useRef(null);
  const recordedMimeRef = useRef("video/webm");

  // Load projects
  useEffect(() => {
    if (token) fetchProjects(token).catch(() => {});
  }, [token, fetchProjects]);

  // Apply the defaults saved on the Settings page
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await studioSettingsAPI.get();
        const r = data?.recording || {};
        const a = data?.audio || {};
        setSettings({
          ...(r.resolution ? { resolution: r.resolution } : {}),
          ...(r.fps ? { fps: r.fps } : {}),
          ...(r.codec ? { codec: r.codec } : {}),
          ...(typeof r.bitrate === "number" ? { bitrate: r.bitrate } : {}),
          ...(r.save_format ? { saveFormat: r.save_format } : {}),
          ...(typeof a.mic === "boolean" ? { includeMic: a.mic } : {}),
          ...(typeof a.system_audio === "boolean" ? { includeSystemAudio: a.system_audio } : {}),
        });
        if (typeof r.countdown_seconds === "number") {
          useRecordingStore.getState().setCountdown(
            r.countdown_enabled === false ? 0 : r.countdown_seconds
          );
        }
        if (data?.magnifier && Object.keys(data.magnifier).length > 0) {
          useMagnifierStore.getState().updateSettings(data.magnifier);
        }
        const p = data?.privacy || {};
        if (p.autoPatterns) usePrivacyStore.getState().setAutoPatterns(p.autoPatterns);
        if (p.style) usePrivacyStore.getState().setStyle(p.style);
      } catch {}
    })();
  }, [token, setSettings]);

  // ── Get supported MIME type ──
  // Always record WebM (VP8/VP9), never MP4 directly. A tab capture is the exact
  // viewport size, which is frequently odd (e.g. 1439x817); H.264 requires even
  // dimensions, so a native MP4 MediaRecorder fails to initialise and stops the
  // recording instantly. VP8/VP9 accept any size. When MP4 is requested we transcode
  // server-side afterwards, where ffmpeg can round the dimensions down to even.
  const getSupportedMimeType = useCallback(() => {
    const codecConfig = CODEC_OPTIONS.find((c) => c.value === settings.codec);
    if (codecConfig && MediaRecorder.isTypeSupported(codecConfig.mime)) {
      return codecConfig.mime;
    }
    const fallbacks = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"];
    for (const mime of fallbacks) {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    }
    return "video/webm";
  }, [settings.codec]);

  // ── Immersive mode ──
  // The studio records via a canvas that reads the panels from the DOM, so it does
  // NOT need the browser's native fullscreen. Native fullscreen on a nested flex
  // element was unreliable here: it collapsed the panel grid and hid the fixed
  // controls bar. Instead we use a CSS overlay (position:fixed + viewport units)
  // that we fully control, so the 4 panels and the controls bar always render.
  // (For true browser-chrome-free fullscreen the presenter can still press F11.)
  const enterFullscreen = async () => {
    setImmersive(true);
  };

  const exitFullscreen = () => {
    setImmersive(false);
  };

  // ── Canvas-based recording (NO getDisplayMedia, NO permission dialog) ──
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);

  // Draw all panels onto a hidden canvas for recording
  const startCanvasCapture = useCallback(() => {
    const container = studioContainerRef.current;
    if (!container) return null;

    const rect = container.getBoundingClientRect();
    const res = RESOLUTIONS.find((r) => r.value === settings.resolution) || RESOLUTIONS[1];
    const canvas = document.createElement("canvas");
    canvas.width = res.width;
    canvas.height = res.height;
    canvasRef.current = canvas;

    const ctx = canvas.getContext("2d");
    const scaleX = res.width / rect.width;
    const scaleY = res.height / rect.height;

    const drawFrame = () => {
      // Clear canvas
      ctx.fillStyle = "#0f0f1a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Find all panel containers
      const panelElements = container.querySelectorAll("[data-panel-id]");
      panelElements.forEach((panelEl) => {
        const panelRect = panelEl.getBoundingClientRect();
        const dx = (panelRect.left - rect.left) * scaleX;
        const dy = (panelRect.top - rect.top) * scaleY;
        const dw = panelRect.width * scaleX;
        const dh = panelRect.height * scaleY;

        // Draw panel background
        ctx.fillStyle = "#12121f";
        ctx.fillRect(dx, dy, dw, dh);
        ctx.strokeStyle = "rgba(255,255,255,0.1)";
        ctx.lineWidth = 1;
        ctx.strokeRect(dx, dy, dw, dh);

        // Draw video elements (webcam, screen share)
        const videos = panelEl.querySelectorAll("video");
        videos.forEach((video) => {
          if (video.readyState >= 2 && video.videoWidth > 0) {
            try {
              const vRect = video.getBoundingClientRect();
              const vx = (vRect.left - rect.left) * scaleX;
              const vy = (vRect.top - rect.top) * scaleY;
              const vw = vRect.width * scaleX;
              const vh = vRect.height * scaleY;
              ctx.drawImage(video, vx, vy, vw, vh);
            } catch {}
          }
        });

        // Draw canvas elements (whiteboard)
        const canvases = panelEl.querySelectorAll("canvas");
        canvases.forEach((c) => {
          if (c === canvas) return; // Skip our own recording canvas
          try {
            const cRect = c.getBoundingClientRect();
            const cx = (cRect.left - rect.left) * scaleX;
            const cy = (cRect.top - rect.top) * scaleY;
            const cw = cRect.width * scaleX;
            const ch = cRect.height * scaleY;
            ctx.drawImage(c, cx, cy, cw, ch);
          } catch {}
        });

        // Draw terminal text content (read from DOM)
        const termDiv = panelEl.querySelector(".font-mono");
        if (termDiv && !panelEl.querySelector("video") && !panelEl.querySelector("canvas:not([class])")) {
          const style = getComputedStyle(termDiv);
          ctx.fillStyle = style.backgroundColor || "#1e1e2e";
          ctx.fillRect(dx, dy + 28 * scaleY, dw, dh - 28 * scaleY);

          // Draw text lines
          const fontSize = Math.max(12, 14 * scaleX);
          ctx.font = `${fontSize}px monospace`;
          ctx.fillStyle = style.color || "#f8f8f2";

          const textContent = termDiv.innerText || termDiv.textContent || "";
          const lines = textContent.split("\n").slice(-50); // Last 50 lines
          lines.forEach((line, li) => {
            const ty = dy + (36 + li * (fontSize + 2)) * scaleY;
            if (ty < dy + dh) {
              ctx.fillText(line.substring(0, 200), dx + 8 * scaleX, ty);
            }
          });
        }

        // Draw embedded objects (PDF) as placeholder with label
        const embeds = panelEl.querySelectorAll("embed, iframe");
        if (embeds.length > 0 && !panelEl.querySelector("video")) {
          ctx.fillStyle = "#2a2a3e";
          ctx.fillRect(dx + 2, dy + 30 * scaleY, dw - 4, dh - 32 * scaleY);
          ctx.fillStyle = "rgba(255,255,255,0.3)";
          ctx.font = `${16 * scaleX}px sans-serif`;
          ctx.textAlign = "center";
          ctx.fillText("📄 PDF / Navigateur", dx + dw / 2, dy + dh / 2);
          ctx.textAlign = "start";
        }
      });

      // Draw Magnifier overlay on canvas if active
      const magState = useMagnifierStore.getState();
      if (magState.isEnabled && !magState.zoomTarget) {
        const cx = magState.position.x * scaleX;
        const cy = magState.position.y * scaleY;
        const dw = magState.size * scaleX;
        const dh = (magState.shape === "circle" ? magState.size : magState.size * 0.6) * scaleY;

        // Draw background blur/darken if active
        if (magState.backgroundBlur) {
          ctx.save();
          ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
        }

        // Draw zoomed contents inside clip path
        ctx.save();
        ctx.beginPath();
        if (magState.shape === "circle") {
          ctx.arc(cx, cy, dw / 2, 0, Math.PI * 2);
        } else {
          ctx.rect(cx - dw / 2, cy - dh / 2, dw, dh);
        }
        ctx.clip();

        // Copy source area from the canvas itself before drawing the border, and stretch it
        const sw = dw / magState.scale;
        const sh = dh / magState.scale;
        ctx.drawImage(canvas, cx - sw / 2, cy - sh / 2, sw, sh, cx - dw / 2, cy - dh / 2, dw, dh);
        ctx.restore();

        // Draw styled border & glow
        ctx.save();
        ctx.strokeStyle = magState.borderColor;
        ctx.lineWidth = magState.borderWidth * scaleX;
        ctx.shadowColor = magState.borderColor;
        ctx.shadowBlur = magState.halo ? 15 * scaleX : 0;
        ctx.beginPath();
        if (magState.shape === "circle") {
          ctx.arc(cx, cy, dw / 2, 0, Math.PI * 2);
        } else {
          ctx.rect(cx - dw / 2, cy - dh / 2, dw, dh);
        }
        ctx.stroke();
        ctx.restore();
      }

      // Draw recording indicator
      ctx.fillStyle = "#ef4444";
      ctx.beginPath();
      ctx.arc(30, 30, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 14px sans-serif";
      ctx.fillText("REC", 45, 35);

      animFrameRef.current = requestAnimationFrame(drawFrame);
    };

    drawFrame();

    // Return canvas stream
    const fps = parseInt(settings.fps) || 30;
    return canvas.captureStream(fps);
  }, [settings.resolution, settings.fps]);

  // ── Screenshot / Capture d'écran ──
  const takeScreenshot = useCallback(async () => {
    const container = studioContainerRef.current;
    if (!container) return;

    try {
      const rect = container.getBoundingClientRect();
      const res = RESOLUTIONS.find((r) => r.value === settings.resolution) || RESOLUTIONS[1];
      const canvas = document.createElement("canvas");
      canvas.width = res.width;
      canvas.height = res.height;
      const ctx = canvas.getContext("2d");
      const scaleX = res.width / rect.width;
      const scaleY = res.height / rect.height;

      // 1) Clear canvas
      ctx.fillStyle = "#0f0f1a";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 2) If active displayStream exists, draw current frame
      let drewDisplayStream = false;
      if (displayStreamRef.current && displayStreamRef.current.getVideoTracks().length > 0) {
        try {
          const video = document.createElement("video");
          video.muted = true;
          video.srcObject = displayStreamRef.current;
          await video.play();
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          drewDisplayStream = true;
        } catch {}
      }

      // 3) Fallback or composite DOM panels if no displayStream
      if (!drewDisplayStream) {
        const panelElements = container.querySelectorAll("[data-panel-id]");
        panelElements.forEach((panelEl) => {
          const panelRect = panelEl.getBoundingClientRect();
          const dx = (panelRect.left - rect.left) * scaleX;
          const dy = (panelRect.top - rect.top) * scaleY;
          const dw = panelRect.width * scaleX;
          const dh = panelRect.height * scaleY;

          ctx.fillStyle = "#12121f";
          ctx.fillRect(dx, dy, dw, dh);
          ctx.strokeStyle = "rgba(255,255,255,0.1)";
          ctx.lineWidth = 1;
          ctx.strokeRect(dx, dy, dw, dh);

          // Videos
          const videos = panelEl.querySelectorAll("video");
          videos.forEach((v) => {
            if (v.readyState >= 2 && v.videoWidth > 0) {
              try {
                const vRect = v.getBoundingClientRect();
                const vx = (vRect.left - rect.left) * scaleX;
                const vy = (vRect.top - rect.top) * scaleY;
                const vw = vRect.width * scaleX;
                const vh = vRect.height * scaleY;
                ctx.drawImage(v, vx, vy, vw, vh);
              } catch {}
            }
          });

          // Canvases
          const canvases = panelEl.querySelectorAll("canvas");
          canvases.forEach((c) => {
            if (c === canvas) return;
            try {
              const cRect = c.getBoundingClientRect();
              const cx = (cRect.left - rect.left) * scaleX;
              const cy = (cRect.top - rect.top) * scaleY;
              const cw = cRect.width * scaleX;
              const ch = cRect.height * scaleY;
              ctx.drawImage(c, cx, cy, cw, ch);
            } catch {}
          });

          // Terminal text
          const termDiv = panelEl.querySelector(".font-mono");
          if (termDiv && !panelEl.querySelector("video") && !panelEl.querySelector("canvas:not([class])")) {
            const style = getComputedStyle(termDiv);
            ctx.fillStyle = style.backgroundColor || "#1e1e2e";
            ctx.fillRect(dx, dy + 28 * scaleY, dw, dh - 28 * scaleY);
            const fontSize = Math.max(12, 14 * scaleX);
            ctx.font = `${fontSize}px monospace`;
            ctx.fillStyle = style.color || "#f8f8f2";
            const textContent = termDiv.innerText || termDiv.textContent || "";
            const lines = textContent.split("\n").slice(-50);
            lines.forEach((line, li) => {
              const ty = dy + (36 + li * (fontSize + 2)) * scaleY;
              if (ty < dy + dh) {
                ctx.fillText(line.substring(0, 200), dx + 8 * scaleX, ty);
              }
            });
          }
        });
      }

      // Convert to Blob and save/download
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error("Échec de la capture d'écran");
          return;
        }

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
        const filename = `takapes_screenshot_${timestamp}.png`;
        const sizeKB = (blob.size / 1024).toFixed(0);

        try {
          if (window.showSaveFilePicker) {
            const handle = await window.showSaveFilePicker({
              suggestedName: filename,
              types: [{ description: "Capture d'écran PNG", accept: { "image/png": [".png"] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            toast.success(`📸 Capture d'écran enregistrée : ${handle.name} (${sizeKB} KB)`);
            return;
          }
        } catch (err) {
          if (err.name === "AbortError") return;
        }

        // Fallback auto download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`📸 Capture d'écran téléchargée : ${filename} (${sizeKB} KB)`);
      }, "image/png");
    } catch (e) {
      toast.error("Erreur lors de la capture d'écran");
    }
  }, [settings.resolution]);

  // ── Start Recording ──
  const startRecording = async () => {
    // 1) Ask to capture the tab/screen FIRST — this must run inside the button's
    //    user activation (before any await delay) or the browser blocks it. Unlike
    //    the old canvas trick, real screen capture records EVERYTHING on screen:
    //    terminals, PDF viewer, embedded browser, videos — all panels, pixel-perfect.
    let displayStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: parseInt(settings.fps) || 30 },
        audio: false,
        preferCurrentTab: true, // Chromium: default the picker to this tab
      });
    } catch (err) {
      toast.error("Partage d'écran refusé ou annulé — enregistrement non démarré");
      return;
    }

    try {
      // 2) Capture mic (optional)
      let micStream = null;
      if (settings.includeMic) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000 },
          });
        } catch {
          toast.info("Microphone non disponible — enregistrement sans micro");
        }
      }

      // 3) Immersive mode (so the captured tab shows only the panels) + countdown
      await enterFullscreen();
      await new Promise((r) => setTimeout(r, 300));
      if (countdown > 0) {
        setCountdownActive(true);
        setCountdownValue(countdown);
        for (let i = countdown; i > 0; i--) {
          setCountdownValue(i);
          await new Promise((r) => setTimeout(r, 1000));
        }
        setCountdownValue(0);
        setCountdownActive(false);
      }

      // 4) Build combined stream (screen video + mic audio)
      displayStreamRef.current = displayStream;
      micStreamRef.current = micStream;
      setCaptureStream(displayStream);
      const combinedTracks = [...displayStream.getVideoTracks()];
      if (micStream) {
        micStream.getAudioTracks().forEach((track) => combinedTracks.push(track));
      }

      const combinedStream = new MediaStream(combinedTracks);
      streamRef.current = combinedStream;
      chunksRef.current = [];

      // If the user ends sharing via the browser's own "Stop sharing" UI, stop too
      const videoTrack = displayStream.getVideoTracks()[0];
      if (videoTrack) videoTrack.addEventListener("ended", () => stopRecording());

      // MediaRecorder
      const mimeType = getSupportedMimeType();
      recordedMimeRef.current = mimeType;
      const bitrateValue = settings.bitrate * 1000;

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: bitrateValue,
        audioBitsPerSecond: 128000,
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onerror = (e) => {
        toast.error(`Erreur d'encodage : ${e.error?.name || "inconnue"}`);
      };

      mediaRecorder.onstop = () => {
        const isMp4 = recordedMimeRef.current.includes("mp4");
        const blob = new Blob(chunksRef.current, { type: isMp4 ? "video/mp4" : "video/webm" });
        clearInterval(timerRef.current);
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        setIsRecording(false);
        setIsPaused(false);
        exitFullscreen();

        // Never save a silently-empty file: surface the failure instead
        if (blob.size === 0) {
          toast.error("Aucune donnée capturée — enregistrement non sauvegardé");
          cleanupStreams();
          return;
        }
        saveLocally(blob);
        cleanupStreams();
      };

      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setLastSaved(null);
      clearMarkers();

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      toast.success("⏺ Enregistrement démarré");
    } catch (err) {
      exitFullscreen();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      displayStream?.getTracks().forEach((t) => t.stop());
      toast.error("Erreur lors du démarrage");
      cleanupStreams();
    }
  };

  // ── Pause / Resume ──
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;
    if (isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
      setIsPaused(false);
      toast.info("Enregistrement repris");
    } else {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setIsPaused(true);
      toast.info("Enregistrement en pause");
    }
  };

  // ── Stop ──
  const stopRecording = () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current?.stop();
    }
    exitFullscreen();
  };

  // ── Save locally ──
  // Transcode a WebM recording to MP4 on the server (ffmpeg), with progress feedback.
  // Returns an MP4 blob, or null if the conversion failed (the WebM is then kept).
  const convertToMp4 = async (blob) => {
    const toastId = toast.loading("Conversion MP4 — envoi de la vidéo…");
    try {
      const { data: job } = await studioConvertAPI.start(blob, "recording.webm", (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          toast.loading(`Conversion MP4 — envoi ${pct}%`, { id: toastId });
        }
      });

      toast.loading("Conversion MP4 — encodage en cours…", { id: toastId });

      let status = job.status;
      while (status === "processing") {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: s } = await studioConvertAPI.status(job.job_id);
        status = s.status;
        if (status === "failed") throw new Error(s.error || "ffmpeg a échoué");
      }

      const { data: mp4 } = await studioConvertAPI.download(job.job_id);
      toast.success("Conversion MP4 terminée", { id: toastId });
      return new Blob([mp4], { type: "video/mp4" });
    } catch (err) {
      toast.error("Conversion MP4 impossible — la vidéo WebM est conservée", { id: toastId });
      return null;
    }
  };

  const saveLocally = async (recordedBlob) => {
    let blob = recordedBlob;
    let ext = blob.type.includes("mp4") ? "mp4" : "webm";

    // MP4 was requested but this browser could only record WebM → transcode server-side
    if (settings.saveFormat === "mp4" && ext === "webm") {
      const converted = await convertToMp4(blob);
      if (converted) {
        blob = converted;
        ext = "mp4";
      }
    }

    const isMp4 = ext === "mp4";
    const mimeType = isMp4 ? "video/mp4" : "video/webm";
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `studio_recording_${timestamp}.${ext}`;
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);

    try {
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: isMp4 ? "Fichier vidéo MP4" : "Fichier vidéo WebM",
            accept: { [mimeType]: [`.${ext}`] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        setLastSaved({ filename: handle.name, size: sizeMB, duration: recordingTime });
        toast.success(`Vidéo sauvegardée : ${handle.name} (${sizeMB} MB)`);

        // Register in backend
        if (token && selectedProject) {
          try {
            await registerRecording(token, selectedProject, {
              project_id: selectedProject,
              filename: handle.name,
              duration_seconds: recordingTime,
              file_size_bytes: blob.size,
              mime_type: mimeType,
              settings: {
                resolution: settings.resolution,
                fps: settings.fps,
                bitrate_kbps: settings.bitrate,
                codec: settings.codec,
                layout_mode: layoutMode,
                panels_count: panels.length,
                include_mic: settings.includeMic,
                include_system_audio: settings.includeSystemAudio,
                markers: markers,
              },
            });
          } catch {}
        }
        return;
      }
    } catch (err) {
      if (err.name === "AbortError") {
        toast.info("Sauvegarde annulée");
        return;
      }
    }

    // Fallback: auto-download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setLastSaved({ filename, size: sizeMB, duration: recordingTime });
    toast.success(`Vidéo téléchargée : ${filename}`);
  };

  // ── Cleanup ──
  const cleanupStreams = () => {
    [displayStreamRef, micStreamRef, streamRef].forEach((ref) => {
      if (ref.current) {
        ref.current.getTracks().forEach((t) => t.stop());
        ref.current = null;
      }
    });
    mediaRecorderRef.current = null;
    setCaptureStream(null);
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      cleanupStreams();
    };
  }, []);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // ── Grid style based on layout mode ──
  const getGridStyle = () => {
    switch (layoutMode) {
      case "2col":
        return { display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr", gap: "4px" };
      case "3col":
        return { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gridTemplateRows: "1fr", gap: "4px" };
      case "2x2":
        return { display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: "4px" };
      case "1+3":
        return { display: "grid", gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr 1fr", gap: "4px" };
      default:
        return { display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr", gap: "4px" };
    }
  };

  const getPanelGridArea = (index) => {
    if (layoutMode === "1+3" && index === 0) {
      return { gridRow: "1 / 4", gridColumn: "1 / 2" };
    }
    return {};
  };

  return (
    <RecordingStudioLayout>
      <div
        ref={studioContainerRef}
        className={`studio-record-root flex flex-col overflow-hidden bg-background ${
          immersive
            ? "fixed inset-0 z-[9998] w-screen h-screen"
            : "h-[calc(100vh-48px)]"
        }`}
      >
        {/* ── Top Bar: Layout selector + Project ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between px-4 py-2.5 bg-card/50 border-b border-border/50 shrink-0"
        >
          {/* Left: Layout presets */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/30 border border-border/50">
              {LAYOUT_PRESETS.map((preset) => {
                const Icon = preset.icon;
                const isActive = layoutMode === preset.id;
                return (
                  <button
                    key={preset.id}
                    onClick={() => setLayoutMode(preset.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    } disabled:opacity-40`}
                    title={preset.desc}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{preset.label}</span>
                  </button>
                );
              })}
            </div>

            <Badge variant="outline" className="text-[10px] gap-1">
              <LayoutGrid className="h-3 w-3" />
              {panels.length} panneau{panels.length > 1 ? "x" : ""}
            </Badge>
          </div>

          {/* Right: Project selector + Status */}
          <div className="flex items-center gap-3">
            {/* Project */}
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-8 w-48 text-xs border-border/50">
                <SelectValue placeholder="Projet (optionnel)" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className={`w-2 h-2 rounded-full ${isPaused ? "bg-amber-400" : "bg-red-500 animate-pulse"}`} />
                <span className="text-xs font-mono font-bold text-red-400">{formatTime(recordingTime)}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Panel Grid ── */}
        <div className="flex-1 min-h-0 overflow-hidden relative">
          <div
            className="w-full h-full p-1"
            style={{
              ...getGridStyle(),
              transform: magnifier.zoomTarget
                ? `scale(${1 + (magnifier.scale - 1) * magnifier.zoomProgress}) translate(${-((magnifier.zoomTarget.x - (studioContainerRef.current?.getBoundingClientRect().width || 1200) / 2) * magnifier.zoomProgress) / magnifier.scale}px, ${-((magnifier.zoomTarget.y - (studioContainerRef.current?.getBoundingClientRect().height || 800) / 2) * magnifier.zoomProgress) / magnifier.scale}px)`
                : "none",
              transformOrigin: "center center",
              transition: isRecording ? "none" : "transform 0.1s ease-out",
            }}
          >
            {panels.map((panel, index) => (
              <motion.div
                key={panel.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                style={getPanelGridArea(index)}
                className="min-h-0 min-w-0"
              >
                <StudioPanel
                  panel={panel}
                  index={index}
                  isRecording={isRecording}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Recording Controls (floating bar at TOP) ── */}
        <RecordingControls
          isRecording={isRecording}
          isPaused={isPaused}
          recordingTime={recordingTime}
          onStart={startRecording}
          onPause={togglePause}
          onResume={togglePause}
          onStop={stopRecording}
          onScreenshot={() => setIsSnipping(true)}
        />

        {/* ── Countdown Overlay ── */}
        <AnimatePresence>
          {countdownActive && countdownValue > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            >
              <motion.div
                key={countdownValue}
                initial={{ scale: 2, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", damping: 15 }}
                className="text-8xl font-bold text-white drop-shadow-2xl"
              >
                {countdownValue}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Success Message ── */}
        <AnimatePresence>
          {lastSaved && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50"
            >
              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-500/20 backdrop-blur-xl border border-emerald-500/30 shadow-xl">
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-300">Vidéo sauvegardée !</p>
                  <p className="text-[11px] text-emerald-300/60">
                    {lastSaved.filename} · {lastSaved.size} MB · {formatTime(lastSaved.duration)}
                  </p>
                </div>
                <button
                  onClick={() => setLastSaved(null)}
                  className="text-emerald-300/40 hover:text-emerald-300 text-xs ml-2"
                >
                  ✕
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Tips (when not recording, bottom-right) ── */}
        {!isRecording && !lastSaved && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="fixed bottom-6 right-6 z-30 max-w-xs"
          >
            <div className="rounded-xl bg-card/80 backdrop-blur-sm border border-border/50 px-4 py-3 shadow-lg">
              <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-amber-500" />
                Comment ça marche
              </h4>
              <ol className="space-y-1 text-[11px] text-muted-foreground">
                <li><strong>1.</strong> Choisissez le nombre d'écrans (2, 3 ou 4)</li>
                <li><strong>2.</strong> Configurez chaque panneau via la barre ▼</li>
                <li><strong>3.</strong> Utilisez le zoom 🔍 pour agrandir le contenu</li>
                <li><strong>4.</strong> Cliquez <strong>Démarrer</strong> pour enregistrer</li>
              </ol>
            </div>
          </motion.div>
        )}
        <SmartMagnifier containerRef={studioContainerRef} recordingStream={captureStream} />
        <PrivacyGuard containerRef={studioContainerRef} />
        <FloombySnipper
          isActive={isSnipping}
          onClose={() => setIsSnipping(false)}
          containerRef={studioContainerRef}
          displayStreamRef={displayStreamRef}
        />
      </div>
    </RecordingStudioLayout>
  );
}
